import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { TaskRow } from "@/components/TaskRow";
import TaskFormDialog from "@/components/TaskFormDialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { ListTodo, Plus } from "lucide-react";
import { toast } from "sonner";

const TABS = [{ v: "all", l: "All" }, { v: "todo", l: "To do" }, { v: "in_progress", l: "In progress" }, { v: "completed", l: "Completed" }];

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [tab, setTab] = useState("all");
  const [mine, setMine] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (tab !== "all") params.status = tab;
    if (mine) params.mine = true;
    api.get("/tasks", { params }).then((r) => setTasks(r.data)).catch(() => {});
  }, [tab, mine]);

  useEffect(() => { load(); }, [load]);

  const onStatus = async (id, status) => {
    try { await api.patch(`/tasks/${id}/status`, { status }); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update status"); }
  };
  const confirmDelete = async () => {
    try { await api.delete(`/tasks/${deleteTarget.id}`); toast.success("Task deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setDeleteTarget(null); }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="tasks-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-gray-900 flex items-center gap-2">
            <ListTodo className="w-7 h-7 text-[#002FA7]" /> Tasks
          </h1>
          <p className="text-sm text-gray-500 mt-1">{tasks.length} task{tasks.length === 1 ? "" : "s"}</p>
        </div>
        <button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="new-task-button"
          className="flex items-center gap-1.5 bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm">
          <Plus className="w-4 h-4" /> New task
        </button>
      </div>

      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex gap-1 bg-gray-100 p-1 rounded-sm">
          {TABS.map((t) => (
            <button key={t.v} onClick={() => setTab(t.v)} data-testid={`task-tab-${t.v}`}
              className={`px-3 py-1.5 rounded-sm text-sm font-semibold transition-colors ${tab === t.v ? "bg-white text-[#002FA7] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
              {t.l}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer" data-testid="tasks-mine-toggle">
          <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} className="accent-[#002FA7]" />
          My tasks only
        </label>
      </div>

      {tasks.length === 0 ? (
        <div className="text-center text-gray-400 text-sm py-16 border border-dashed border-gray-200 rounded-sm" data-testid="tasks-empty">
          No tasks here yet. Create one to get started.
        </div>
      ) : (
        <div className="space-y-2" data-testid="tasks-list">
          {tasks.map((t) => (
            <TaskRow key={t.id} task={t} onStatus={onStatus} onEdit={(task) => { setEditing(task); setDialogOpen(true); }} onDelete={setDeleteTarget} />
          ))}
        </div>
      )}

      <TaskFormDialog open={dialogOpen} onOpenChange={setDialogOpen} task={editing} onSaved={load} />

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-sm" data-testid="delete-task-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete task?</AlertDialogTitle>
            <AlertDialogDescription><strong>{deleteTarget?.title}</strong> will be permanently deleted.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-task-button" className="bg-red-600 hover:bg-red-700 rounded-sm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
